import { forwardRef, useEffect, useState, useRef, useImperativeHandle } from "react"
import { Upload, Wand2, ChevronLeft, ChevronRight, Music, Scissors } from "lucide-react"
import VideoTrimmer from "../Editor/VideoTrimmer"
import { Button } from "components/ui/button"
import { Card, CardContent } from "components/ui/card"
import { Input } from "components/ui/input"
import { Skeleton } from "components/ui/skeleton"
import axiosInstance from "axiosInstance"
import { uploadFile } from "utils"
import { useToast } from "components/ui/toaster/use-toast"

// const sceneJson = [
//   {
//     path: "/mnt/data\\slideshow_stock_1_18_0.mp4",
//     chunk:
//       "you would have seen these types of subtitles on many trending videos",
//     end_time: 5.86,
//     keywords: "subtitles",
//     sentence:
//       "you would have seen these types of subtitles on many trending videos",
//     start_time: 0.94,
//     selected_asset: {
//       api: "video_search_pixabay",
//       link: "https://cdn.pixabay.com/video/2021/11/02/94332-643067712_large.mp4",
//       type: "video",
//       title: "news, tag, titles, subtitles, green screen",
//       reason:
//         "The keyword is 'subtitles,' and the chunk indicates a focus on trending videos. Sentence 2 ('news, tag, titles, subtitles, green screen') is relevant as it directly mentions 'subtitles' and also aligns with the context of video production due to its association with 'green screen,' which suggests a potential for dynamic and visually engaging video content related to subtitles.",
//     },
//     structure_type: "broll",
//     alternative_assets: [
//       {
//         api: "video_search_pixabay",
//         link: "https://cdn.pixabay.com/video/2021/11/02/94332-643067712_large.mp4",
//         type: "video",
//         title: "news, tag, titles, subtitles, green screen",
//       },
//       {
//         api: "video_search_pixabay",
//         link: "https://cdn.pixabay.com/video/2022/06/24/121984-724732207_large.mp4",
//         type: "video",
//         title:
//           "confetti, confetti from left, trending confetti, celebration, fun, colorful, colorful confetti, party, party poppers, birthday poppers, colorful party poppers, overlay video, overlay poppers",
//       },
//       {
//         api: "video_search_pixabay",
//         link: "https://cdn.pixabay.com/video/2022/06/25/122025-724732303_large.mp4",
//         type: "video",
//         title: "youtube, like, facebook, international, process, trend, viral",
//       },
//       {
//         api: "video_search_pixabay",
//         link: "https://cdn.pixabay.com/video/2021/11/02/94332-643067712_large.mp4",
//         type: "video",
//         title: "news, tag, titles, subtitles, green screen",
//       },
//       {
//         api: "video_search_envato",
//         link: "https://previews.customer.envatousercontent.com/h264-video-previews/c9f03226-bbf5-4905-beb1-04d4b7e1a2fb/56266959.mp4",
//         type: "video",
//         title: "Text Animation | FCPX",
//       },
//       {
//         api: "video_search_envato",
//         link: "https://previews.customer.envatousercontent.com/video-previews/34aed3a6-121c-42b1-8922-3aea41d5db9c/36dc326c-274b-4672-bfad-421055d3a226/watermarked_preview.mp4",
//         type: "video",
//         title: "Valentine Festival Text Animation",
//       },
//       {
//         api: "video_search_envato",
//         link: "https://previews.customer.envatousercontent.com/h264-video-previews/068b3ecb-58fb-4280-9c6b-5b682959939c/56301676.mp4",
//         type: "video",
//         title: "Minimal Text Animation \\ AE",
//       },
//       {
//         api: "video_search_envato",
//         link: "https://previews.customer.envatousercontent.com/h264-video-previews/327ccf66-f2ff-4926-b18e-129791fce177/56286802.mp4",
//         type: "video",
//         title: "Text Animation - Lower Thirds",
//       },
//     ],
//     word_level_timestamps: [
//       {
//         end: 1.18,
//         word: "you",
//         start: 0.94,
//       },
//       {
//         end: 1.5,
//         word: "would",
//         start: 1.18,
//       },
//       {
//         end: 1.74,
//         word: "have",
//         start: 1.5,
//       },
//       {
//         end: 2.26,
//         word: "seen",
//         start: 1.74,
//       },
//       {
//         end: 2.74,
//         word: "these",
//         start: 2.42,
//       },
//       {
//         end: 3.18,
//         word: "types",
//         start: 2.74,
//       },
//       {
//         end: 3.34,
//         word: "of",
//         start: 3.18,
//       },
//       {
//         end: 4.3,
//         word: "subtitles",
//         start: 3.34,
//       },
//       {
//         end: 4.54,
//         word: "on",
//         start: 4.34,
//       },
//       {
//         end: 4.86,
//         word: "many",
//         start: 4.54,
//       },
//       {
//         end: 5.26,
//         word: "trending",
//         start: 4.86,
//       },
//       {
//         end: 5.86,
//         word: "videos",
//         start: 5.26,
//       },
//     ],
//   },
// ];

const formatMapping = {
  "B-Roll": "broll",
  Card: "customized_broll",
  "Split Screen": "maya",
  Speaker: "dhruv",
}

const reverseFormatMapping = {
  broll: "B-Roll",
  customized_broll: "Card",
  maya: "Split Screen",
  dhruv: "Speaker",
}

function VisualEditor({ setCurrentStep, videoID }, ref) {
  const { toast } = useToast()
  const [chunks, setChunks] = useState()
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [bgmFile, setBgmFile] = useState(null)

  const handleBgmUpload = async (file) => {
    try {
      setIsLoading(true)
      setBgmFile(file)
      const url = await uploadFile(file, null, 'audio')
      if (url) {
        const updatedChunk = {
          ...chunks[currentChunkIndex],
          background_music: url,
        }
        updateChunk(updatedChunk)
      }
      toast({
        title: "Success",
        description: "BGM audio uploaded successfully!",
      })
    } catch (error) {
      console.error("Error uploading BGM:", error)
      toast({
        title: "Error",
        description: "Failed to upload BGM audio",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const removeBgm = () => {
    setBgmFile(null)
    const updatedChunk = {
      ...chunks[currentChunkIndex],
      background_music: null,
    }
    updateChunk(updatedChunk)
  }

  // const videoID=50;

  const fetchSceneJson = async () => {
    if (!videoID) {
      alert("Please upload the video first")
      setCurrentStep(0)
      return
    }
    console.log("videoID", videoID)
    const config = {
      headers: { "content-type": "application/json" },
    }

    const response = await axiosInstance.get(
      process.env.REACT_APP_BACKEND_URL + `/microservices/marketier/get_scenejson/${videoID}`,
      config,
    )

    console.log('Scene json: ', response.data)
    setChunks(response.data)
  }

  const generate_image = async (query) => {
    const config = {
      headers: { "content-type": "application/json" },
    }

    const response = await axiosInstance.get(
      process.env.REACT_APP_BACKEND_URL + `/microservices/marketier/generate_img/${query}`,
      config,
    )

    console.log('Generated image response: ', response.data)
    return response.data.img_link
  }

  const search_assets = async (query) => {
    const config = {
      headers: { "content-type": "application/json" },
    }

    const response = await axiosInstance.get(
      process.env.REACT_APP_BACKEND_URL + `/microservices/marketier/search_assets/${query}`,
      config,
    )

    // console.log(response.data);
    return response.data
  }

  const update_scenejson = async () => {
    console.log("json", chunks)
    if (!videoID) {
      alert("Please upload the video first")
      setCurrentStep(0)
      return
    }

    try {
      setIsLoading(true)
      const data = {
        scene_json: chunks,
      }
      const config = {
        headers: { "content-type": "application/json" },
      }

      const response = await axiosInstance.patch(
        process.env.REACT_APP_BACKEND_URL + `/microservices/marketier/update_scenejson/${videoID}`,
        data,
        config,
      )

      console.log(response.data)
      toast({
        title: "Success",
        description: "Scene JSON updated successfully!",
      })
    } catch (error) {
      console.error("Error updating scene JSON:", error)
      toast({
        title: "Error",
        description: "Failed to update Scene JSON",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (videoID) {
      fetchSceneJson()
    } else {
      alert("Please upload the video first")
      setCurrentStep(0)
    }
    // fetchSceneJson();
  }, [])

  // const updateChunk = (index, updatedChunk) => {
  //   setChunks((prevChunks) => {
  //     const newChunks = [...prevChunks];
  //     newChunks[index] = { ...newChunks[index], ...updatedChunk };
  //     return newChunks;
  //   });
  // };

  const updateChunk = (updatedChunk) => {
    const newChunks = [...chunks]
    newChunks[currentChunkIndex] = {
      ...newChunks[currentChunkIndex],
      ...updatedChunk,
    }
    setChunks(newChunks)
  }

  // const updateSearchQuery = (index, newQuery) => {
  //   // Simulating asset update based on new search query
  //   const updatedChunk = {
  //     ...chunks[index],
  //     keywords: newQuery,
  //     selected_asset: {
  //       ...chunks[index].selected_asset,
  //       link: `https://example.com/new-asset-for-${newQuery}.jpg`,
  //       type: "image",
  //     },
  //     alternative_assets: [
  //       {
  //         link: `https://example.com/option1-for-${newQuery}.jpg`,
  //         type: "image",
  //       },
  //       {
  //         link: `https://example.com/option2-for-${newQuery}.mp4`,
  //         type: "video",
  //       },
  //       {
  //         link: `https://example.com/option3-for-${newQuery}.jpg`,
  //         type: "image",
  //       },
  //       {
  //         link: `https://example.com/option4-for-${newQuery}.mp4`,
  //         type: "video",
  //       },
  //     ],
  //   };
  //   updateChunk(index, updatedChunk);
  // };

  const updateSearchQuery = async (newQuery) => {
    // Simulating asset update based on new search query
    const assets = await JSON.parse(await search_assets(newQuery))
    console.log("assets", assets)
    const updatedChunk = {
      ...chunks[currentChunkIndex],
      // store the term on the canonical `keywords` field so the chunk's displayed keywords are kept in sync with the server/state.
      keywords: newQuery,
      selected_asset: assets[0],
      alternative_assets: assets,
    }
    updateChunk(updatedChunk)
  }

  // const handleCustomAsset = async(index,file) => {
  //   // Simulating adding a custom asset
  //   const customAssetLink = await uploadFile(file)
  //   const updatedChunk = {
  //     ...chunks[index],
  //     selected_asset: {
  //       ...chunks[index].selected_asset,
  //       link: customAssetLink,
  //       type: file.type.startsWith('image/') ? "image" : "video",
  //     },
  //   };
  //   updateChunk(index, updatedChunk);
  // };

  const handleCustomAsset = async (file) => {
    // Simulating adding a custom asset
    const customAssetLink = await uploadFile(file)
    const updatedChunk = {
      ...chunks[currentChunkIndex],
      selected_asset: {
        ...chunks[currentChunkIndex].selected_asset,
        link: customAssetLink,
        type: file.type.startsWith("image/") ? "image" : "video",
      },
    }
    updateChunk(updatedChunk)
  }

  // Accept an optional promptOverride so children can pass the local input
  // value (useful when user edited the input but didn't click Update).
  const handleGenerateAI = async (promptOverride) => {
    // Simulating AI image generation
    const prompt = promptOverride || chunks[currentChunkIndex].keywords || chunks[currentChunkIndex].searchQuery
    console.log("Generating image for prompt:", prompt)
    const link = await generate_image(prompt)
    console.log("Generated image link:", prompt)
    const updatedChunk = {
      ...chunks[currentChunkIndex],
      selected_asset: {
        ...chunks[currentChunkIndex].selected_asset,
        link: link,
        type: "image",
      },
    }
    updateChunk(updatedChunk)
  }

  const handleScroll = (e) => {
    const scrollPosition = e.target.scrollTop
    const chunkHeight = e.target.scrollHeight / chunks.length
    const newChunkIndex = Math.min(Math.floor(scrollPosition / chunkHeight), chunks.length - 1)
    setCurrentChunkIndex(newChunkIndex)
  }

  // Expose update_scenejson through the ref
  useImperativeHandle(ref, () => ({
    updateSceneJson: update_scenejson,
  }))

  return (
    chunks &&
    chunks.length > 0 && (
      <div className="container mx-auto px-2 md:px-4 max-w-4xl h-screen flex flex-col 2xl:block 2xl:h-auto">
        {/* <div className="space-y-6">
        {chunks.map((chunk, index) => (
          <ChunkCard
            key={index}
            chunk={chunk}
            onUpdate={(updatedChunk) => updateChunk(index, updatedChunk)}
            onSearchQueryUpdate={(newQuery) =>
              updateSearchQuery(index, newQuery)
            }
            onCustomAsset={(file) => handleCustomAsset(index,file)}
            onGenerateAI={() => handleGenerateAI(index)}
          />
        ))}
      </div> */}
        {/* <div className="flex mb-4">
          <div className="flex h-5 items-center">
            <Checkbox id="subtitles" checked={subtitles} onChange={() => setSubtitles(!subtitles)} />
          </div>
          <div className="flex flex-col ml-2">
            <Label htmlFor="subtitles">Subtitles</Label>
            <div className="text-gray-500 dark:text-gray-300">
              <span className="text-xs font-normal">
                This will <span className="font-medium">Turn Off</span> auto subtitles for{" "}
                <span className="font-medium">B-Roll</span>, <span className="font-medium">Split Screen</span> and{" "}
                <span className="font-medium">Speaker</span> formats.
              </span>
            </div>
          </div>
        </div> */}
        <div className="flex-grow overflow-y-auto 2xl:flex-grow-0 2xl:max-h-[70vh]" onScroll={handleScroll}>
          <div style={{ height: `${chunks.length * 100}vh` }}>
            <div className="sticky top-0">
              <ChunkCard
                chunk={chunks[currentChunkIndex]}
                onUpdate={updateChunk}
                onSearchQueryUpdate={updateSearchQuery}
                onCustomAsset={(file) => handleCustomAsset(file)}
                onGenerateAI={handleGenerateAI}
                isLoading={isLoading} // Pass loading state
                setIsLoading={setIsLoading} // Pass setter function
              />
            </div>
          </div>
        </div>
        <div className="py-3 border-t">
          <div className="flex items-center justify-between px-4">
            <span className="text-sm text-muted-foreground">Background Music</span>
            {bgmFile ? (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-md">
                  <Music className="w-4 h-4" />
                  <span className="text-sm truncate max-w-[150px]">{bgmFile.name}</span>
                </div>
                <button
                  onClick={removeBgm}
                  className="text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100 px-3 py-1 rounded-full text-sm font-medium transition-colors"
                  type="button"
                >
                  Remove
                </button>
              </div>
            ) : (
              <Button variant="outline" size="sm" asChild>
                <label className="cursor-pointer">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Audio
                  <input
                    type="file"
                    accept="audio/*"
                    onChange={(e) => {
                      const file = e.target.files && e.target.files[0]
                      if (file) {
                        handleBgmUpload(file)
                      }
                      e.target.value = null
                    }}
                    className="hidden"
                  />
                </label>
              </Button>
            )}
          </div>
        </div>
        <div className="py-4 flex justify-center">
          <Button
            onClick={update_scenejson}
            size="lg"
            className="text-white bg-green-500 rounded-full hover:bg-green-600"
          >
            Save
          </Button>
        </div>
      </div>
    )
  )
}

export default forwardRef(VisualEditor)

function ChunkCard({
  chunk,
  onUpdate,
  onSearchQueryUpdate,
  onCustomAsset,
  onGenerateAI,
  isLoading, // Receive loading state as prop
  setIsLoading, // Receive setter function as prop
}) {
  const [searchQuery, setSearchQuery] = useState(chunk.keywords || "")
  // Remove local isLoading state since we're now using the prop
  const [assetPage, setAssetPage] = useState(0)
  const fileInputRef = useRef(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isTrimming, setIsTrimming] = useState(false)
  const [trimConfig, setTrimConfig] = useState(null)

  const openTrimmer = () => {
    const asset = chunk.selected_asset
    if (!asset || asset.type !== "video") {
      alert("Only videos can be trimmed.")
      return
    }
    
    // Calculate duration from chunk start/end times
    // chunk.end_time and chunk.start_time are simple numbers in the example json
    const duration = chunk.end_time - chunk.start_time

    if (duration <= 0) {
      alert("Invalid chunk duration. Cannot trim.")
      return
    }

    setTrimConfig({
      url: asset.link,
      duration: duration
    })
    setIsTrimming(true)
  }

  const handleTrimComplete = async (blob) => {
    try {
      setIsLoading(true)
      const file = new File([blob], `trimmed_video_${Date.now()}.mp4`, { type: 'video/mp4' })
      const url = await uploadFile(file)
      
      if (url) {
        onUpdate({
            selected_asset: {
            ...chunk.selected_asset,
            link: url,
            type: "video",
          },
        })
      }
    } catch (error) {
       console.error("Trim upload failed", error)
       alert("Failed to upload trimmed video")
    } finally {
      setIsLoading(false)
      setIsTrimming(false)
      setTrimConfig(null)
    }
  }

  // Keep local input in sync when the parent provides a different chunk.
  // The ChunkCard component is reused (sticky), so the internal state must update when `chunk` changes; otherwise the input shows the previous chunk's value for all chunks.
  useEffect(() => {
    setSearchQuery(chunk?.keywords || "")
  }, [chunk])

  const handleSearchQueryUpdate = () => {
    // Use the parent's loading state so the overlay shows for the whole screen while assets are fetched. 
    // Await the parent's async update so we can sync the local input after the chunk in parent state updates.
    const run = async () => {
      try {
        setIsLoading(true)
        await onSearchQueryUpdate(searchQuery)
        // after parent updates chunk, reflect the submitted value locally so
        // the input shows the saved keyword immediately.
        setSearchQuery(searchQuery)
      } catch (e) {
        console.error("search update failed", e)
      } finally {
        setIsLoading(false)
      }
    }
    run()
  }

  const handleFormatChange = (newFormat) => {
    // Map the UI format back to the sceneJson structure_type
    const structureType = formatMapping[newFormat]
    onUpdate({ structure_type: structureType })
  }

  const handleAssetSelect = (asset) => {
    onUpdate({
      selected_asset: {
        ...chunk.selected_asset,
        link: asset.link,
        type: asset.type,
      },
    })
  }

  // Get the UI format name for display
  const displayFormat = reverseFormatMapping[chunk.structure_type] || chunk.structure_type

  const assetsPerPage = window.innerWidth < 768 ? 6 : 8

  const uniqueAssets = [
    { link: chunk.selected_asset.link, type: chunk.selected_asset.type },
    ...chunk.alternative_assets.filter(
      (asset) =>
        asset.link !== chunk.selected_asset.link &&
        !chunk.alternative_assets.find(
          (a, index) => a.link === asset.link && chunk.alternative_assets.indexOf(asset) > index,
        ),
    ),
  ]

  const totalPages = Math.ceil(uniqueAssets.length / assetsPerPage)

  const handleNextPage = () => {
    setAssetPage((prev) => (prev + 1) % totalPages)
  }

  const handlePrevPage = () => {
    setAssetPage((prev) => (prev - 1 + totalPages) % totalPages)
  }

  const displayedAssets = uniqueAssets.slice(assetPage * assetsPerPage, (assetPage + 1) * assetsPerPage)

  return (
    <Card>
      <CardContent className="p-6 h-screen">
        {/* Loading overlay */}
        {isLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="relative w-64 h-64 flex items-center justify-center">
              {/* Subtle glow effect behind logo */}
              <div className="absolute inset-0 rounded-full bg-white dark:bg-blue-400 filter blur-xl opacity-20 animate-pulse-subtle"></div>

              {/* Rotating logo - direct without container */}
              <div className="relative z-10 animate-spin-slow">
                {/* Replace with your actual logo */}
                <img
                  src="https://assets-editai.azureedge.net/images/editai-favicon.png"
                  alt="Edit AI Logo"
                  className="object-contain w-56 h-56"
                />
              </div>

              {/* Static text below the rotating logo */}
              <div className="absolute -bottom-12 w-full text-center">
                <p className="text-lg font-semibold text-white">Slaying🤘...</p>
              </div>
            </div>
          </div>
        )}

        {isTrimming && trimConfig && (
          <div className="absolute inset-0 z-50">
            <VideoTrimmer 
                videoSrc={trimConfig.url} 
                chunkDuration={trimConfig.duration} 
                onClose={() => setIsTrimming(false)}
                onTrimComplete={handleTrimComplete}
            />
          </div>
        )}

        <p className="text-lg font-semibold mb-4">{chunk.chunk}</p>
        <div className="flex items-center space-x-2 mb-6">
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search query"
            className="flex-grow"
          />
          <Button onClick={handleSearchQueryUpdate} className="text-white bg-blue-500">
            Update
          </Button>
        </div>
        <div className="flex flex-col md:flex-row md:space-x-6 space-y-4 md:space-y-0">
          <div className="w-full md:w-1/5">
            <div className="flex flex-col space-x-2 md:space-x-0 md:space-y-2 overflow-x-auto pb-2 md:pb-0">
              {["B-Roll", "Card", "Split Screen", "Speaker"].map((format) => (
                <Button
                  key={format}
                  variant={displayFormat === format ? "default" : "outline"}
                  onClick={() =>
                    handleFormatChange(format)}
                  className={`justify-start ${displayFormat === format ? "bg-blue-700 text-white" : "bg-white text-blue-700"
                    }`}
                >
                  {format}
                </Button>
              ))}
            </div>
          </div>
          <div className="w-full md:w-4/5">
            {displayFormat !== "Speaker" && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2 md:gap-4">
                  {isLoading
                    ? Array(6)
                      .fill()
                      .map((_, i) => <Skeleton key={i} className="w-full aspect-square rounded-lg" />)
                    : displayedAssets.map((asset, i) => (
                      <AssetBox
                        key={i}
                        asset={asset.link}
                        type={asset.type}
                        isSelected={asset.link === chunk.selected_asset.link}
                        onSelect={() =>
                          handleAssetSelect({
                            link: asset.link,
                            type: asset.type,
                          })
                        }
                      />
                    ))}
                </div>
                <div className="flex justify-between items-center">
                  <Button onClick={handlePrevPage} variant="outline" size="icon">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span>
                    {assetPage + 1} / {totalPages}
                  </span>
                  <Button onClick={handleNextPage} variant="outline" size="icon">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-col md:flex-row justify-center space-y-2 md:space-y-0 md:space-x-4">
                  <Button
                    variant="outline"
                    className="w-full py-2 flex flex-row items-center justify-center bg-transparent"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  >
                    <Upload className="w-6 h-6" />
                    <span className="text-xs ml-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,video/*"
                        onChange={(e) => {
                          const file = e.target.files && e.target.files[0]
                          if (file) {
                            onCustomAsset(file)
                          }
                          e.target.value = null
                        }}
                        className="hidden"
                      />
                      Upload
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full py-2 flex flex-row items-center justify-center bg-transparent"
                    onClick={openTrimmer}
                  >
                    <Scissors className="w-6 h-6" />
                    <span className="text-xs ml-2">Trim</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full py-2 flex flex-row items-center justify-center bg-transparent"
                    onClick={() => {
                      const run = async () => {
                        try {
                          setIsGenerating(true)
                          // pass current edited input so generation uses latest text
                          await onGenerateAI(searchQuery)
                        } catch (e) {
                          console.error("Generate AI failed", e)
                        } finally {
                          setIsGenerating(false)
                        }
                      }
                      run()
                    }}
                  >
                    <Wand2 className="w-6 h-6" />
                    <span className="text-xs ml-2">
                      {isGenerating ? (
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                        </svg>
                      ) : (
                        'Generate AI'
                      )}
                    </span>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function AssetBox({ asset, type, isSelected, onSelect }) {
  const videoRef = useRef(null)
  const hoverTimeoutRef = useRef(null)
  const [isHovered, setIsHovered] = useState(false)

  const handleMouseEnter = () => {
    clearTimeout(hoverTimeoutRef.current) // Clear any existing timeout
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(true)
      if (videoRef.current && type === "video") {
        videoRef.current.play().catch((error) => {
          console.warn("Video play interrupted:", error)
        })
      }
    }, 100)
  }

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeoutRef.current) // Clear any existing timeout
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false)
      if (videoRef.current && type === "video") {
        videoRef.current.pause()
        videoRef.current.currentTime = 0 // Reset video to the start
      }
    }, 100)
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeout(hoverTimeoutRef.current)
    }
  }, [])

  return (
    <div
      className={`w-full aspect-square rounded-lg overflow-hidden cursor-pointer ${isSelected ? "ring-2 ring-primary" : ""
        }`}
      onClick={onSelect}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-selected={isSelected}
    >
      {type === "image" ? (
        <img src={asset || "/placeholder.svg"} alt="Asset" className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <video
          ref={videoRef}
          src={asset}
          className="w-full h-full object-cover"
          loop
          muted
          playsInline
          preload="metadata"
        />
      )}
    </div>
  )
}
